import React from 'react';
import { useIdioma } from '../../i18n/idioma';
import { Search, X } from 'lucide-react';

interface SearchInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    /** Ancho del contenedor. Por defecto crece en móvil y fija 260px en desktop. */
    className?: string;
}

/**
 * SearchInput — Sistema 2.0.
 * Campo de búsqueda de toolbar: 40px, ícono dentro, botón de limpiar cuando
 * hay texto. Mismo foco que el resto del sistema (borde navy + halo amarillo).
 */
export const SearchInput: React.FC<SearchInputProps> = ({
    value, onChange, placeholder, className = '', ...props
}) => {
    const { t } = useIdioma();
    /* El marcador por defecto era `'Search…'` escrito a mano: en una app en
       español, cada buscador sin `placeholder` propio decía «Search». */
    const marcador = placeholder ?? t('comun.buscarPlaceholder');
    return (
    /* `h-10` fija la altura del contenedor. Sin ella, dentro de un grid o flex
       con `items-stretch` el wrapper se estira a la altura de la fila y el
       `top-1/2` del ícono lo centra respecto a la CAJA ESTIRADA, no al campo:
       la lupa acaba flotando debajo del texto. */
    <div className={`relative h-10 ${className || 'w-full md:w-[260px]'}`}>
        <Search
            size={15}
            strokeWidth={2}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-acero-400 pointer-events-none"
        />
        <input
            type="search"
            value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={marcador}
            className="w-full h-10 pl-9 pr-8 bg-white border border-acero-300 rounded-xl text-sm text-navy-950
                placeholder:text-acero-400 transition-[border-color,box-shadow] duration-150
                focus:outline-none focus:border-navy-600 focus:ring-2 focus:ring-brand-400/55
                [&::-webkit-search-cancel-button]:appearance-none"
            {...props}
        />
        {value && (
            <button
                type="button"
                onClick={() => onChange('')}
                aria-label={t('aria.limpiarBusqueda')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-acero-400 hover:text-navy-950 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
            >
                <X size={14} strokeWidth={2.5} />
            </button>
        )}
        </div>
    );
};
